// Economics Unit Tests, Exercises, and Resources
// Vanuatu National Syllabus - Years 11-13

// Assessment Structure
export const economicsAssessment = {
  year11: {
    internalExamination: {
      weight: 60,
      format: "Written examination (3 hours)",
      sections: "Short Answer, Paragraph Responses, Data Analysis",
      strandWeights: {
        strand1: 19,
        strand2: 16,
        strand3: 13,
        strand4: 12
      }
    },
    continuousAssessment: {
      weight: 40,
      tasks: 5,
      components: [
        { name: "Topic Development Task (TDT)", count: 2, weight: 16 },
        { name: "Continuous Assessment Task (CAT)", count: 2, weight: 16 },
        { name: "Minor Tasks", count: 1, weight: 8 }
      ]
    },
    skillLevels: {
      unistructural: "Recall and identify basic economic concepts",
      multistructural: "Describe and explain relationships between concepts",
      relational: "Apply economic theory to analyze situations",
      extendedAbstract: "Evaluate policies and create solutions"
    }
  },
  year12: {
    externalExamination: {
      weight: 60,
      format: "Written examination (3 hours)",
      sections: "Multiple Choice, Short Answer, Extended Response",
      totalMarks: 60,
      strandWeights: {
        strand1: 19,
        strand2: 16,
        strand3: 13,
        strand4: 12
      }
    },
    internalAssessment: {
      weight: 40,
      tasks: 3,
      components: [
        { name: "Major Internal Task", count: 1, weight: 20 },
        { name: "Minor Internal Tasks", count: 2, weight: 20 }
      ]
    }
  },
  year13: {
    externalExamination: {
      weight: 60,
      format: "Written examination (3 hours)",
      totalMarks: 60,
      sections: "Objective, Short Paragraph, Graph Interpretation, Calculations",
      strandWeights: {
        strand1: 25,
        strand2: 15,
        strand3: 16,
        strand4: 4
      }
    },
    internalAssessment: {
      weight: 40,
      tasks: 3,
      components: [
        { name: "Research Project", count: 1, weight: 15 },
        { name: "Data Analysis Task", count: 1, weight: 15 },
        { name: "Policy Evaluation", count: 1, weight: 10 }
      ]
    }
  }
};

// Year 11 Unit Tests
export const year11EconomicsUnitTests = {
  term1: [
    {
      id: "y11-eco-t1-test1",
      title: "Introduction to Economics Test",
      strand: "ECO1",
      topics: ["Scarcity", "Opportunity Cost", "PPC"],
      duration: "45 minutes",
      totalMarks: 30,
      instructions: [
        "Answer all questions",
        "Show all working for calculations",
        "Draw diagrams clearly and label axes"
      ],
      sections: [
        {
          name: "Section A: Multiple Choice",
          marks: 10,
          questions: [
            { id: 1, question: "The economic problem exists because:", options: ["A) Wants are unlimited but resources are limited", "B) Resources are unlimited", "C) There is no scarcity", "D) Governments control all resources"], marks: 1, answer: "A" },
            { id: 2, question: "Opportunity cost is best defined as:", options: ["A) The monetary cost of a choice", "B) The next best alternative forgone", "C) Total cost of production", "D) Government spending"], marks: 1, answer: "B" },
            { id: 3, question: "Which is NOT a factor of production?", options: ["A) Land", "B) Labor", "C) Money", "D) Capital"], marks: 1, answer: "C" },
            { id: 4, question: "The reward for labor is:", options: ["A) Rent", "B) Interest", "C) Wages", "D) Profit"], marks: 1, answer: "C" },
            { id: 5, question: "A point inside the PPC indicates:", options: ["A) Economic growth", "B) Underutilization of resources", "C) Full employment", "D) Impossible production"], marks: 1, answer: "B" }
          ]
        },
        {
          name: "Section B: Short Answer",
          marks: 10,
          questions: [
            { id: 6, question: "Define scarcity and explain why it creates the need for choice.", marks: 3 },
            { id: 7, question: "List the four factors of production and their corresponding rewards.", marks: 4 },
            { id: 8, question: "Explain the difference between goods and services with examples.", marks: 3 }
          ]
        },
        {
          name: "Section C: Extended Response",
          marks: 10,
          questions: [
            { id: 9, question: "Draw a Production Possibility Curve. Label it clearly and explain: (a) What a point on the curve represents (b) What a point inside the curve represents (c) What would cause the curve to shift outward", marks: 10 }
          ]
        }
      ]
    },
    {
      id: "y11-eco-t1-test2",
      title: "Demand Analysis Test",
      strand: "ECO1",
      topics: ["Law of Demand", "Demand Curves", "Elasticity"],
      duration: "50 minutes",
      totalMarks: 35,
      instructions: [
        "Answer all questions",
        "Use diagrams where appropriate",
        "Show all calculations"
      ],
      sections: [
        {
          name: "Section A: Definitions",
          marks: 10,
          questions: [
            { id: 1, question: "Define demand.", marks: 2 },
            { id: 2, question: "State the law of demand.", marks: 2 },
            { id: 3, question: "What is price elasticity of demand?", marks: 2 },
            { id: 4, question: "Distinguish between a movement along and a shift of the demand curve.", marks: 4 }
          ]
        },
        {
          name: "Section B: Application",
          marks: 15,
          questions: [
            { id: 5, question: "List and explain THREE non-price factors that affect demand.", marks: 6 },
            { id: 6, question: "Draw a demand curve and show: (a) A decrease in price causing an increase in quantity demanded (b) An increase in income causing an increase in demand", marks: 6 },
            { id: 7, question: "Classify the following goods as luxury, necessity, or inferior goods: (a) Rice (b) Designer clothing (c) Public transport when income rises", marks: 3 }
          ]
        },
        {
          name: "Section C: Calculation",
          marks: 10,
          questions: [
            { id: 8, question: "The price of coconuts falls from 50 VT to 40 VT, and quantity demanded increases from 100 to 140 units. Calculate the price elasticity of demand and state whether demand is elastic or inelastic.", marks: 10 }
          ]
        }
      ]
    }
  ],
  term2: [
    {
      id: "y11-eco-t2-test1",
      title: "Supply and Market Equilibrium Test",
      strand: "ECO1",
      topics: ["Law of Supply", "Market Equilibrium", "Price Controls"],
      duration: "55 minutes",
      totalMarks: 40,
      instructions: [
        "Answer all questions",
        "Draw clear diagrams with proper labels",
        "Explain your reasoning"
      ],
      sections: [
        {
          name: "Section A: Knowledge",
          marks: 12,
          questions: [
            { id: 1, question: "Define supply.", marks: 2 },
            { id: 2, question: "State the law of supply.", marks: 2 },
            { id: 3, question: "What is market equilibrium?", marks: 2 },
            { id: 4, question: "Define: (a) Minimum price (b) Maximum price", marks: 4 },
            { id: 5, question: "What is a subsidy?", marks: 2 }
          ]
        },
        {
          name: "Section B: Diagrams and Analysis",
          marks: 18,
          questions: [
            { id: 6, question: "Draw a supply and demand diagram showing: (a) Initial equilibrium price and quantity (b) The effect of an increase in supply on equilibrium", marks: 8 },
            { id: 7, question: "Using a diagram, explain the effect of a government setting a price ceiling below equilibrium price.", marks: 5 },
            { id: 8, question: "Using a diagram, explain the effect of a government subsidy to producers.", marks: 5 }
          ]
        },
        {
          name: "Section C: Extended Response",
          marks: 10,
          questions: [
            { id: 9, question: "Discuss the advantages and disadvantages of government price controls in the Vanuatu economy. Give examples.", marks: 10 }
          ]
        }
      ]
    }
  ],
  term3: [
    {
      id: "y11-eco-t3-test1",
      title: "Government and Money Test",
      strand: "ECO2 & ECO3",
      topics: ["Government Functions", "Taxation", "Money", "Inflation"],
      duration: "60 minutes",
      totalMarks: 45,
      instructions: [
        "Answer all questions",
        "Provide examples from Vanuatu where possible",
        "Show all calculations"
      ],
      sections: [
        {
          name: "Section A: Government",
          marks: 15,
          questions: [
            { id: 1, question: "List THREE reasons why governments intervene in the economy.", marks: 3 },
            { id: 2, question: "Distinguish between direct and indirect taxes with examples.", marks: 4 },
            { id: 3, question: "Explain the difference between progressive, regressive, and proportional tax systems.", marks: 6 },
            { id: 4, question: "Define merit goods and demerit goods with examples.", marks: 2 }
          ]
        },
        {
          name: "Section B: Money",
          marks: 15,
          questions: [
            { id: 5, question: "Explain the FOUR functions of money.", marks: 4 },
            { id: 6, question: "List FOUR characteristics of good money.", marks: 4 },
            { id: 7, question: "Using Fisher's Equation (MV = PQ), explain what happens to the price level if the money supply increases.", marks: 4 },
            { id: 8, question: "Name TWO financial institutions in Vanuatu.", marks: 3 }
          ]
        },
        {
          name: "Section C: Inflation",
          marks: 15,
          questions: [
            { id: 9, question: "Define inflation and state how it is measured.", marks: 3 },
            { id: 10, question: "Explain the difference between demand-pull and cost-push inflation.", marks: 6 },
            { id: 11, question: "Discuss THREE economic effects of inflation on households and businesses.", marks: 6 }
          ]
        }
      ]
    }
  ],
  term4: [
    {
      id: "y11-eco-t4-test1",
      title: "Trade and Payments Test",
      strand: "ECO4",
      topics: ["International Trade", "Balance of Payments", "Exchange Rates"],
      duration: "50 minutes",
      totalMarks: 35,
      instructions: [
        "Answer all questions",
        "Use Vanuatu examples where relevant",
        "Show your working"
      ],
      sections: [
        {
          name: "Section A: Trade",
          marks: 12,
          questions: [
            { id: 1, question: "Give THREE reasons why countries trade with each other.", marks: 3 },
            { id: 2, question: "Distinguish between domestic trade and international trade.", marks: 3 },
            { id: 3, question: "List Vanuatu's THREE major exports.", marks: 3 },
            { id: 4, question: "List Vanuatu's THREE major imports.", marks: 3 }
          ]
        },
        {
          name: "Section B: Balance of Payments",
          marks: 12,
          questions: [
            { id: 5, question: "Define balance of payments.", marks: 2 },
            { id: 6, question: "Distinguish between the current account and the capital account.", marks: 4 },
            { id: 7, question: "Explain the importance of: (a) Foreign investment (b) Foreign aid (c) Remittances to Vanuatu's economy", marks: 6 }
          ]
        },
        {
          name: "Section C: Exchange Rates",
          marks: 11,
          questions: [
            { id: 8, question: "Define exchange rate.", marks: 2 },
            { id: 9, question: "Compare fixed and floating exchange rate systems.", marks: 4 },
            { id: 10, question: "Explain TWO factors that affect exchange rates.", marks: 3 },
            { id: 11, question: "If the Vanuatu Vatu depreciates, what happens to: (a) Exports (b) Imports", marks: 2 }
          ]
        }
      ]
    }
  ]
};

// Year 12 Unit Tests
export const year12EconomicsUnitTests = {
  term1: [
    {
      id: "y12-eco-t1-test1",
      title: "Advanced Microeconomics Test",
      strand: "ECO1",
      topics: ["Elasticity Calculations", "Cost Analysis", "Production"],
      duration: "60 minutes",
      totalMarks: 50,
      instructions: [
        "Answer all questions",
        "Show all calculations",
        "Use diagrams where appropriate"
      ],
      sections: [
        {
          name: "Section A: Elasticity",
          marks: 20,
          questions: [
            { id: 1, question: "Calculate the price elasticity of demand using the mid-point formula: Price falls from $20 to $16, Quantity rises from 100 to 140.", marks: 6 },
            { id: 2, question: "Explain THREE factors that affect the price elasticity of demand.", marks: 6 },
            { id: 3, question: "If PED = 0.5, is demand elastic or inelastic? Explain the implication for total revenue if price increases.", marks: 4 },
            { id: 4, question: "Distinguish between elastic and inelastic supply with examples.", marks: 4 }
          ]
        },
        {
          name: "Section B: Costs and Production",
          marks: 18,
          questions: [
            { id: 5, question: "Define: (a) Total Cost (b) Average Cost (c) Marginal Cost", marks: 6 },
            { id: 6, question: "A firm has the following data: Output: 100, 200, 300. Total Cost: 500, 800, 1200. Calculate Average Cost and Marginal Cost for each output level.", marks: 8 },
            { id: 7, question: "Explain the relationship between marginal cost and average cost curves.", marks: 4 }
          ]
        },
        {
          name: "Section C: Production and Efficiency",
          marks: 12,
          questions: [
            { id: 8, question: "Explain economies of scale and diseconomies of scale.", marks: 6 },
            { id: 9, question: "Using a PPC diagram, explain what an outward shift represents and what could cause it.", marks: 6 }
          ]
        }
      ]
    }
  ],
  term2: [
    {
      id: "y12-eco-t2-test1",
      title: "Market Structures and Government Test",
      strand: "ECO1 & ECO2",
      topics: ["Market Types", "Government Policy", "Taxation"],
      duration: "65 minutes",
      totalMarks: 55,
      instructions: [
        "Answer all questions",
        "Compare and contrast where asked",
        "Use real-world examples"
      ],
      sections: [
        {
          name: "Section A: Market Structures",
          marks: 20,
          questions: [
            { id: 1, question: "Compare the features of perfect competition and monopoly.", marks: 8 },
            { id: 2, question: "What is oligopoly? Give TWO examples from Vanuatu or the Pacific.", marks: 4 },
            { id: 3, question: "Explain how equilibrium price and quantity are determined in a competitive market.", marks: 4 },
            { id: 4, question: "Using a diagram, show excess demand and explain how the market corrects this.", marks: 4 }
          ]
        },
        {
          name: "Section B: Government Policy",
          marks: 20,
          questions: [
            { id: 5, question: "Explain the components of a government budget.", marks: 4 },
            { id: 6, question: "Apply the principles of taxation (equity, efficiency, simplicity) to evaluate the VAT system.", marks: 8 },
            { id: 7, question: "Explain how the government can address the problem of demerit goods.", marks: 4 },
            { id: 8, question: "Distinguish between monetary and fiscal policy.", marks: 4 }
          ]
        },
        {
          name: "Section C: Extended Analysis",
          marks: 15,
          questions: [
            { id: 9, question: "Discuss the effectiveness of government intervention in addressing market failure. Use examples of externalities.", marks: 15 }
          ]
        }
      ]
    }
  ]
};

// Year 13 Unit Tests
export const year13EconomicsUnitTests = {
  term1: [
    {
      id: "y13-eco-t1-test1",
      title: "Advanced Microeconomic Theory Test",
      strand: "ECO1",
      topics: ["Efficiency", "Utility", "All Elasticities", "Surplus"],
      duration: "75 minutes",
      totalMarks: 60,
      instructions: [
        "Answer all questions",
        "Show all calculations clearly",
        "Use economic terminology accurately"
      ],
      sections: [
        {
          name: "Section A: Efficiency and PPC",
          marks: 15,
          questions: [
            { id: 1, question: "Distinguish between productive efficiency and allocative efficiency.", marks: 4 },
            { id: 2, question: "On a PPC diagram, identify: (a) A productively efficient point (b) An allocatively efficient point (c) An inefficient point", marks: 6 },
            { id: 3, question: "Explain the difference between an increasing cost PPC and a constant cost PPC.", marks: 5 }
          ]
        },
        {
          name: "Section B: Elasticity Calculations",
          marks: 20,
          questions: [
            { id: 4, question: "Calculate price elasticity of demand using the mid-point formula: P1=$10, P2=$8, Q1=200, Q2=280", marks: 5 },
            { id: 5, question: "The price of tea rises from $5 to $6, and the demand for coffee increases from 500 to 600 units. Calculate cross-elasticity of demand and interpret the result.", marks: 5 },
            { id: 6, question: "Income rises from $30,000 to $35,000, and quantity demanded rises from 100 to 120. Calculate income elasticity and classify the good.", marks: 5 },
            { id: 7, question: "Explain how the government can use elasticity information when deciding which goods to tax.", marks: 5 }
          ]
        },
        {
          name: "Section C: Surplus and Welfare",
          marks: 15,
          questions: [
            { id: 8, question: "Define consumer surplus and producer surplus.", marks: 4 },
            { id: 9, question: "Using a diagram, show consumer surplus and producer surplus at market equilibrium.", marks: 5 },
            { id: 10, question: "Explain how a tax creates deadweight loss. Show on a diagram.", marks: 6 }
          ]
        },
        {
          name: "Section D: Extended Response",
          marks: 10,
          questions: [
            { id: 11, question: "Analyze the conditions under which a price ceiling benefits consumers and the conditions under which it harms them.", marks: 10 }
          ]
        }
      ]
    }
  ],
  term2: [
    {
      id: "y13-eco-t2-test1",
      title: "Market Structures and Market Failure Test",
      strand: "ECO1 & ECO2",
      topics: ["Profit Maximization", "Externalities", "Public Goods"],
      duration: "70 minutes",
      totalMarks: 55,
      instructions: [
        "Answer all questions",
        "Draw diagrams clearly",
        "Evaluate critically"
      ],
      sections: [
        {
          name: "Section A: Market Structures",
          marks: 20,
          questions: [
            { id: 1, question: "Explain the profit maximization rule (MR = MC).", marks: 4 },
            { id: 2, question: "Using diagrams, compare short-run equilibrium in perfect competition and monopoly.", marks: 8 },
            { id: 3, question: "Why do economists consider perfect competition allocatively efficient?", marks: 4 },
            { id: 4, question: "Explain why monopolies may be inefficient.", marks: 4 }
          ]
        },
        {
          name: "Section B: Market Failure",
          marks: 20,
          questions: [
            { id: 5, question: "Define market failure and list THREE causes.", marks: 4 },
            { id: 6, question: "Using a diagram, explain a negative externality of production.", marks: 6 },
            { id: 7, question: "Using a diagram, explain a positive externality of consumption.", marks: 5 },
            { id: 8, question: "Compare private equilibrium and social equilibrium when externalities exist.", marks: 5 }
          ]
        },
        {
          name: "Section C: Extended Analysis",
          marks: 15,
          questions: [
            { id: 9, question: "Evaluate government policies to address negative externalities. Consider taxes, regulations, and tradeable permits.", marks: 15 }
          ]
        }
      ]
    }
  ],
  term3: [
    {
      id: "y13-eco-t3-test1",
      title: "Macroeconomic Policy Test",
      strand: "ECO3",
      topics: ["AD-AS Model", "Fiscal Policy", "Monetary Policy"],
      duration: "75 minutes",
      totalMarks: 60,
      instructions: [
        "Answer all questions",
        "Use AD-AS diagrams where appropriate",
        "Analyze policy effects"
      ],
      sections: [
        {
          name: "Section A: National Income",
          marks: 15,
          questions: [
            { id: 1, question: "Distinguish between nominal GDP and real GDP.", marks: 3 },
            { id: 2, question: "Explain the THREE approaches to measuring GDP.", marks: 6 },
            { id: 3, question: "List the FOUR components of aggregate demand.", marks: 4 },
            { id: 4, question: "What factors cause the aggregate demand curve to shift?", marks: 2 }
          ]
        },
        {
          name: "Section B: AD-AS Model",
          marks: 20,
          questions: [
            { id: 5, question: "Draw an AD-AS diagram showing macroeconomic equilibrium.", marks: 5 },
            { id: 6, question: "Using AD-AS, show a recessionary gap and explain its implications.", marks: 5 },
            { id: 7, question: "Using AD-AS, show an inflationary gap and explain its implications.", marks: 5 },
            { id: 8, question: "Analyze the effect of an increase in government spending on the AD-AS model.", marks: 5 }
          ]
        },
        {
          name: "Section C: Policy Analysis",
          marks: 15,
          questions: [
            { id: 9, question: "Compare fiscal policy and monetary policy in terms of: (a) Tools (b) Effectiveness (c) Limitations", marks: 9 },
            { id: 10, question: "Explain how the central bank uses the interest rate to control inflation.", marks: 6 }
          ]
        },
        {
          name: "Section D: Extended Response",
          marks: 10,
          questions: [
            { id: 11, question: "Evaluate the effectiveness of monetary policy in a small open economy like Vanuatu.", marks: 10 }
          ]
        }
      ]
    }
  ]
};

// Exercises
export const year11EconomicsExercises = [
  {
    id: "y11-eco-ex1",
    title: "PPC Practice",
    strand: "ECO1",
    type: "Graphing Exercise",
    questions: [
      "Draw a PPC showing the trade-off between coconuts and taro production in Vanuatu",
      "Show the effect of a cyclone destroying agricultural land",
      "Show the effect of improved farming technology"
    ]
  },
  {
    id: "y11-eco-ex2",
    title: "Demand and Supply Practice",
    strand: "ECO1",
    type: "Diagram Exercise",
    questions: [
      "Draw supply and demand curves for kava",
      "Show the effect of a successful tourist season on kava demand",
      "Show the effect of a drought on kava supply",
      "Identify the new equilibrium price and quantity"
    ]
  },
  {
    id: "y11-eco-ex3",
    title: "Elasticity Calculations",
    strand: "ECO1",
    type: "Calculation Exercise",
    questions: [
      "Price rises from 100 VT to 120 VT, quantity demanded falls from 500 to 400. Calculate PED.",
      "Is demand elastic or inelastic?",
      "What happens to total revenue when price increases?"
    ]
  }
];

export const year12EconomicsExercises = [
  {
    id: "y12-eco-ex1",
    title: "Cost Analysis Practice",
    strand: "ECO1",
    type: "Calculation Exercise",
    questions: [
      "Given TC = 100 + 5Q + 0.1Q², calculate TC, AC, and MC for Q = 10, 20, 30",
      "Draw the AC and MC curves",
      "Identify the minimum average cost output level"
    ]
  },
  {
    id: "y12-eco-ex2",
    title: "Market Structure Comparison",
    strand: "ECO1",
    type: "Analysis Exercise",
    questions: [
      "Compare pricing behavior in perfect competition vs monopoly",
      "Explain why monopolies can earn long-run economic profits",
      "Analyze the Vanuatu telecommunications market structure"
    ]
  }
];

export const year13EconomicsExercises = [
  {
    id: "y13-eco-ex1",
    title: "Surplus and Welfare Analysis",
    strand: "ECO1",
    type: "Graphing Exercise",
    questions: [
      "Draw a market diagram showing CS, PS, and total welfare",
      "Show the effect of a $5 per unit tax on welfare",
      "Calculate and shade the deadweight loss"
    ]
  },
  {
    id: "y13-eco-ex2",
    title: "AD-AS Model Practice",
    strand: "ECO3",
    type: "Analysis Exercise",
    questions: [
      "Draw AD-AS showing full employment equilibrium",
      "Show the short-run and long-run effects of an increase in AD",
      "Analyze the appropriate policy response to a recessionary gap"
    ]
  }
];

// Resources
export const economicsResources = [
  {
    title: "Vanuatu Economic Data",
    type: "Reference",
    description: "Statistics on GDP, inflation, trade from VNSO",
    unit: "All Strands",
    year: "All Years"
  },
  {
    title: "Reserve Bank of Vanuatu Reports",
    type: "Primary Source",
    description: "Quarterly economic bulletins and monetary policy statements",
    unit: "ECO3",
    year: "Year 12-13"
  },
  {
    title: "World Bank Vanuatu Data",
    type: "Reference",
    description: "Development indicators and country profiles",
    unit: "ECO4",
    year: "All Years"
  },
  {
    title: "PPC and Market Diagram Templates",
    type: "Worksheet",
    description: "Blank diagram templates for practice",
    unit: "ECO1",
    year: "Year 11"
  },
  {
    title: "Elasticity Formula Sheet",
    type: "Reference Card",
    description: "All elasticity formulas with examples",
    unit: "ECO1",
    year: "Year 11-13"
  },
  {
    title: "AD-AS Model Guide",
    type: "Study Guide",
    description: "Comprehensive guide to macroeconomic equilibrium",
    unit: "ECO3",
    year: "Year 13"
  },
  {
    title: "Past Examination Papers",
    type: "Practice Papers",
    description: "Collection of past SPBEA economics papers",
    unit: "All",
    year: "Year 12-13"
  },
  {
    title: "Economics Vocabulary List",
    type: "Glossary",
    description: "Key terms and definitions for each strand",
    unit: "All",
    year: "All Years"
  }
];

// Test Blueprints
export const economicsTestBlueprints = {
  year11: {
    internalExamination: {
      duration: "3 hours",
      totalMarks: 60,
      structure: [
        { section: "A", type: "Short Answer", marks: 20, questions: 10 },
        { section: "B", type: "Paragraph Responses", marks: 24, questions: 4 },
        { section: "C", type: "Extended Response", marks: 16, questions: 2 }
      ],
      strandDistribution: {
        ECO1: "19%",
        ECO2: "16%",
        ECO3: "13%",
        ECO4: "12%"
      }
    }
  },
  year12: {
    externalExamination: {
      duration: "3 hours",
      totalMarks: 60,
      structure: [
        { section: "A", type: "Multiple Choice/Short Answer", marks: 15, questions: 15 },
        { section: "B", type: "Structured Questions", marks: 25, questions: 5 },
        { section: "C", type: "Extended Response", marks: 20, questions: 2 }
      ],
      strandDistribution: {
        ECO1: "19%",
        ECO2: "16%",
        ECO3: "13%",
        ECO4: "12%"
      }
    }
  },
  year13: {
    externalExamination: {
      duration: "3 hours",
      totalMarks: 60,
      structure: [
        { section: "A", type: "Objective/Short Answer", marks: 15, questions: 15 },
        { section: "B", type: "Structured Questions", marks: 25, questions: 5 },
        { section: "C", type: "Extended Analysis", marks: 20, questions: 2 }
      ],
      strandDistribution: {
        ECO1: "25%",
        ECO2: "15%",
        ECO3: "16%",
        ECO4: "4%"
      },
      skillLevels: {
        knowledge: "20%",
        application: "35%",
        analysis: "30%",
        evaluation: "15%"
      }
    }
  }
};

export default {
  economicsAssessment,
  year11EconomicsUnitTests,
  year12EconomicsUnitTests,
  year13EconomicsUnitTests,
  year11EconomicsExercises,
  year12EconomicsExercises,
  year13EconomicsExercises,
  economicsResources,
  economicsTestBlueprints
};
